import React from 'react';
import { MapPinIcon, CalendarIcon, UserCheckIcon } from 'lucide-react';
import { Container, Section, SectionHeader, Kicker, Reveal } from '../components/ui/Primitives';
import { Button } from '../components/ui/Button';
import { HeroArcs } from '../components/sections/HeroVisual';
import { PhotoStrip } from '../components/sections/PhotoStrip';
import { PHOTOS } from '../data/photos';
import { Seo } from '../components/Seo';
import { PositionCard } from '../components/careers/PositionCard';
import { OPEN_POSITIONS, WHY_JOIN, INTERNSHIP_TERMS } from '../data/careers';
import { breadcrumbSchema } from '../lib/seo';
export function Careers() {
  return <>
      <Seo title="Careers at Cavalla Solution | Join Our Team in Kigali" description="Internships in Kigali across engineering, data and AI, research, communications and partnerships. Real responsibility from day one on software and research for African governments." path="/careers" jsonLd={[breadcrumbSchema([{
      name: 'Home',
      path: '/'
    }, {
      name: 'Careers',
      path: '/careers'
    }])]} data-id="element-2301" data-mp="31gg0muak9yv6" />

      {/* Hero */}
      <section className="relative isolate overflow-hidden surface-dark" data-id="element-2302" data-mp="1ep2ob80vy4rt">
        <HeroArcs className="pointer-events-none absolute -right-12 top-0 h-[360px] w-[360px] opacity-80" data-id="element-2303" data-mp="1alwlybgw8xqm" />
        <Container className="relative py-16 sm:py-24" data-id="element-2304" data-mp="15o4j4mepnr7n">
          <Kicker data-id="element-2305" data-mp="22csnwh1f1hz4">Careers</Kicker>
          <h1 className="mt-4 max-w-4xl font-display text-[32px] font-light leading-[1.15] text-white sm:text-[48px]" data-id="element-2306" data-mp="22c16ca4nlkru">
            Build what governments need. Grow while you do it.
          </h1>
          <p className="mt-6 max-w-2xl text-[17px] leading-relaxed text-white/72 sm:text-[19px]" data-id="element-2307" data-mp="1tio4giom38tm">
            Cavalla is a small, close team in Kigali building software and research that helps African governments
            deliver on their promises. We are hiring interns who want real responsibility from day one.
          </p>
          <div className="mt-9" data-id="element-2308" data-mp="2h7yup68c5kti">
            <Button href="#positions" size="lg" shape="pill" data-id="element-2309" data-mp="305savmm7l5tf">
              See open positions
            </Button>
          </div>
        </Container>
      </section>

      {/* About the team */}
      <Section aria-labelledby="team-heading" data-id="element-2310" data-mp="2re9tx9hfulza">
        <div className="grid gap-10 lg:grid-cols-[0.9fr_1.1fr] lg:gap-20" data-id="element-2311" data-mp="2rdi289qyp8ea">
          <SectionHeader id="team-heading" kicker="About the team" title="We are a young company doing serious work." data-id="element-2312" data-mp="2ve0to2oy0cfy" />
          <div className="space-y-5 text-[17px] leading-relaxed text-body" data-id="element-2313" data-mp="2he9azonhe7vm">
            <p data-id="element-2314" data-mp="2mfgj5otjd92t">
              Cavalla builds tools that track whether national development plans actually turn into results on the
              ground, and we advise governments and development partners on how to make their investments count. Our
              first platform, Cavalla Gov, is already in pilot with Liberia’s Ministry of Finance, and we are expanding.
            </p>
            <p data-id="element-2315" data-mp="ape4ju9tsn1l">
              The team is small enough that everyone knows what everyone else is working on. You will not be filing
              someone else’s paperwork. You will ship code, write research that gets read, or talk to real clients,
              depending on what you came here to do.
            </p>
          </div>
        </div>
      </Section>

      <PhotoStrip id="team-photos" title="Where the work takes you" intro="Analysis in our Kigali office, workshops with government teams, partner meetings and time in the field." photos={[PHOTOS.officeAnalysts, PHOTOS.friendsInternational, PHOTOS.ethiopiaPortrait]} data-id="element-2316" data-mp="2068djyd1c90x" />

      {/* Why join */}
      <Section tone="mist" aria-labelledby="why-heading" data-id="element-2317" data-mp="2nw2ogadsnnij">
        <SectionHeader id="why-heading" kicker="Why join Cavalla" title="What an internship here actually means." data-id="element-2318" data-mp="2ntgj8hedlk3h" />
        <div className="mt-12 grid gap-px overflow-hidden rounded-md border border-line bg-line sm:grid-cols-2" data-id="element-2319" data-mp="thjly7t95k0i">
          {WHY_JOIN.map(item => <div key={item.title} className="bg-mist p-7 sm:p-9" data-id="element-2320" data-mp="ok9nk2nvxy0j">
              <h3 className="font-display text-[22px] leading-snug text-ink" data-id="element-2321" data-mp="qy52ofwp66vg">{item.title}</h3>
              <p className="mt-3 text-[16px] leading-relaxed text-body" data-id="element-2322" data-mp="uyfi0j6dq9mz">{item.body}</p>
            </div>)}
        </div>
      </Section>

      {/* Culture */}
      <Section aria-labelledby="culture-heading" data-id="element-2323" data-mp="zw8qjiv5nfqf">
        <div className="mx-auto max-w-3xl" data-id="element-2324" data-mp="14w3gnk73w1af">
          <Kicker data-id="element-2325" data-mp="14usd9lhkovju">Culture</Kicker>
          <h2 id="culture-heading" className="mt-3 font-display text-[28px] leading-tight text-ink sm:text-[40px]" data-id="element-2326" data-mp="79wa44r8ryvf">
            Cavalla runs on trust, not surveillance.
          </h2>
          <p className="mt-6 text-[18px] leading-relaxed text-body" data-id="element-2327" data-mp="h7tzwwor23q5">
            We set clear goals, give people the room to meet them, and back each other up when things get hard. We
            celebrate wins together, we are honest when something is not working, and we treat every person on the
            team as family. If you are the kind of person who does good work because you care about the work, you
            will fit in.
          </p>
        </div>
      </Section>

      {/* Positions */}
      <Section id="positions" tone="mist" aria-labelledby="positions-heading" data-id="element-2328" data-mp="3d51b881q6z3f">
        <div className="flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between" data-id="element-2329" data-mp="3d2x31dwsndxc">
          <SectionHeader id="positions-heading" kicker={`${OPEN_POSITIONS.length} open internships`} title="Internship positions." data-id="element-2330" data-mp="387hvf3pdqe9d" />
          <ul className="grid gap-3 text-[15px] text-body sm:grid-cols-3 lg:max-w-2xl lg:gap-6" data-id="element-2331" data-mp="2z6bbekniulbd">
            <li className="flex items-start gap-2" data-id="element-2332" data-mp="2ucyznb3xy901">
              <MapPinIcon className="mt-0.5 h-4 w-4 shrink-0 text-gold" aria-hidden="true" data-id="element-2333" data-mp="2penrd4tao6jx" />
              Based in {INTERNSHIP_TERMS.location}
            </li>
            <li className="flex items-start gap-2" data-id="element-2334" data-mp="2kdzq7qbcq9bz">
              <CalendarIcon className="mt-0.5 h-4 w-4 shrink-0 text-gold" aria-hidden="true" data-id="element-2335" data-mp="2fh9cvaqxlru9" />
              {INTERNSHIP_TERMS.duration}, {INTERNSHIP_TERMS.fullTimeNote}
            </li>
            <li className="flex items-start gap-2" data-id="element-2336" data-mp="20tsqqnl28r9q">
              <UserCheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-gold" aria-hidden="true" data-id="element-2337" data-mp="20weyan7yb6ry" />
              A named senior lead and defined deliverables from week one
            </li>
          </ul>
        </div>

        {OPEN_POSITIONS.length > 0 ? <ul className="mt-12 grid gap-5 md:grid-cols-2" data-id="element-2338" data-mp="1wrxh8vsz77kv">
            {OPEN_POSITIONS.map((position, index) => <li key={position.slug} data-id="element-2339" data-mp="2kjkzyqg33cc2" data-mpi={index}>
                <Reveal delay={Math.min(index * 0.04, 0.2)} className="h-full" data-id="element-2340" data-mp="2kknquydeperz">
                  <PositionCard position={position} data-id="element-2341" data-mp="2oj3hrdclz7ao" />
                </Reveal>
              </li>)}
          </ul> : <p className="mt-12 max-w-prose text-[17px] text-body" data-id="element-2342" data-mp="2frbfvr1nh6hp">
            We have no open positions right now. Follow us on LinkedIn to hear first when we are hiring.
          </p>}

        <div className="mt-12 flex flex-col gap-4 border-t border-line pt-8 sm:flex-row sm:items-center sm:justify-between" data-id="element-2343" data-mp="387m31juhu0k3">
          <p className="max-w-xl text-[16px] text-body" data-id="element-2344" data-mp="3hlwc87zrqqhg">
            Not sure which role fits? Apply to the one closest to your strengths and tell us in your cover letter.
          </p>
          <Button to="/careers/apply" variant="secondary" className="shrink-0" data-id="element-2345" data-mp="3rd0lqs6odj4w">
            Go to the application form
          </Button>
        </div>
      </Section>
    </>;
}